import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useCallback, useMemo } from 'react';
import { adminApi } from '../api/adminApi';
import type { Module, Grade, ModuleLevel } from '../types';
import { SUBJECT_OPTIONS, ALL_SUBJECT_OPTIONS, GRADE_OPTIONS, REGISTRABLE_PROGRAMME_OPTIONS } from '@padcanvas/shared';
import { ModulesTableView } from '@shared-ui/curriculum-view';
import { IntegerInput } from '@shared-ui/components';
import { ModuleImportDialog } from './ModuleImportDialog';
import { ModulePitfallsEditor } from './ModulePitfallsEditor';
import { IS_PROD_ENV } from '../api/auth';
import { useIsNonPublicTenant } from '../hooks/useMyTenant';
import './Modules.css';

export function Modules() {
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Module> | null>(null);
  const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [filterSubject, setFilterSubject] = useState<string>(SUBJECT_OPTIONS[0]);
  const [filterGrade, setFilterGrade] = useState<string>('');
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  // The taxonomy is ONE tree shared by every tenant, and prod's copy is built by
  // the dev→prod sync. So: no authoring in prod at all (a module created here is
  // a row the sync never made and will not carry back), and no import/export for
  // a school admin, whose console is a read-only view of the syllabus.
  // docs/FeatureDesigns/multi-tenancy/tenant-admin-console.md §4 — VIEW-ONLY.
  const isNonPublicTenant = useIsNonPublicTenant();
  const canAuthorModules = !IS_PROD_ENV;
  const canImportExport = !isNonPublicTenant;

  const queryClient = useQueryClient();

  // Hierarchy loads fast (no counts). Modules/topics change rarely — don't
  // refetch on every tab switch. Save/delete/import invalidate ['modules'].
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['modules', filterSubject, filterGrade],
    // full=true → full Module objects incl. inactive, for the list + edit modal.
    queryFn: () => adminApi.getModules({
      subject: filterSubject || undefined,
      grade: filterGrade || undefined,
      full: true,
    }),
    placeholderData: (prev) => prev,
    staleTime: 10 * 60 * 1000,  // 10 min
    gcTime: 30 * 60 * 1000,     // keep cache across tab switches
  });

  // Per-module question counts loaded separately (the slow GROUP BY) so the
  // hierarchy renders immediately; the "# Questions" column fills in when ready.
  const { data: countsData } = useQuery({
    queryKey: ['module-counts', filterSubject, filterGrade],
    queryFn: () => adminApi.getModules({
      subject: filterSubject || undefined,
      grade: filterGrade || undefined,
      full: true,
      withCounts: true,
    }),
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Both counts ride together. Only the counts query asks for them (`withCounts`),
  // so anything not copied across here keeps the undefined the rows arrived with
  // — which is why Teacher Questions read 0 on every row while the total beside
  // it was right, including on modules whose questions are ALL teacher-authored.
  const countById = useMemo(
    () => new Map((countsData?.modules ?? []).map(m => [m.id, {
      questionCount: m.questionCount ?? 0,
      teacherQuestionCount: m.teacherQuestionCount ?? 0,
    }])),
    [countsData],
  );
  const modulesWithCounts = useMemo(
    () => (data?.modules ?? []).map(m => ({
      ...m,
      questionCount: countById.get(m.id)?.questionCount ?? 0,
      teacherQuestionCount: countById.get(m.id)?.teacherQuestionCount ?? 0,
    })),
    [data, countById],
  );

  const saveMutation = useMutation({
    mutationFn: (module: Partial<Module>) => adminApi.saveModule(module),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      setSaveStatus({ type: 'success', message: 'Module saved successfully' });
      setEditingModule(null);
      setIsCreating(false);
      setEditForm(null);
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      setSaveStatus({ type: 'error', message: `Save failed: ${error.message}` });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteModule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['modules'] });
      setSaveStatus({ type: 'success', message: 'Module deleted successfully' });
      setTimeout(() => setSaveStatus(null), 3000);
    },
    onError: (error: Error) => {
      setSaveStatus({ type: 'error', message: `Delete failed: ${error.message}` });
    },
  });

  const handleCreateNew = () => {
    setIsCreating(true);
    setEditingModule(null);
    setEditForm({
      readableId: '',
      displayName: '',
      subject: 'maths',
      gradeBoards: [],
      description: '',
      sortOrder: 0,
      isActive: true,
      moduleLevel: 'chapter' as ModuleLevel,
      isTopModule: false,
      parentModuleId: undefined,
      instructions: undefined,
    });
  };

  const handleEditClick = (module: Module) => {
    setEditingModule(module);
    setIsCreating(false);
    setEditForm({ ...module });
  };

  const handleDeleteClick = (module: Module) => {
    if (confirm(`Are you sure you want to delete module "${module.displayName}"?`)) {
      deleteMutation.mutate(module.id);
    }
  };

  const handleFormChange = (field: keyof Module, value: unknown) => {
    if (!editForm) return;
    setEditForm({ ...editForm, [field]: value });
  };

  // ── gradeBoards: [grade, exams[]] rows, grouped by grade ──
  // Always send an array — `undefined` would be dropped by JSON and the save merges
  // over the stored module, so removing the last row has to post [] to clear it.
  const setGradeBoards = (rows: [Grade, string[]][]) => {
    if (!editForm) return;
    setEditForm({ ...editForm, gradeBoards: rows });
  };
  const addGradeBoardRow = () => {
    const rows = editForm?.gradeBoards ?? [];
    const used = new Set(rows.map(r => r[0]));
    const grade = (GRADE_OPTIONS.find(c => !used.has(c)) ?? GRADE_OPTIONS[0]) as Grade;
    setGradeBoards([...rows, [grade, []]]);
  };
  const setGradeBoardGrade = (i: number, grade: Grade) => {
    const rows = (editForm?.gradeBoards ?? []).map((r, idx) => (idx === i ? [grade, r[1]] as [Grade, string[]] : r));
    setGradeBoards(rows);
  };
  const toggleGradeBoardExam = (i: number, exam: string) => {
    const rows = (editForm?.gradeBoards ?? []).map((r, idx) => {
      if (idx !== i) return r;
      const exams = r[1].includes(exam) ? r[1].filter(e => e !== exam) : [...r[1], exam];
      return [r[0], exams] as [Grade, string[]];
    });
    setGradeBoards(rows);
  };
  const removeGradeBoardRow = (i: number) => {
    setGradeBoards((editForm?.gradeBoards ?? []).filter((_, idx) => idx !== i));
  };

  const handleSave = () => {
    if (!editForm) return;
    saveMutation.mutate(editForm);
  };

  const handleCancel = () => {
    setEditingModule(null);
    setIsCreating(false);
    setEditForm(null);
    setSaveStatus(null);
  };

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const result = await adminApi.exportModules();
      const json = JSON.stringify(result, null, 2);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `modules_export_${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setSaveStatus({ type: 'error', message: `Export failed: ${err instanceof Error ? err.message : String(err)}` });
    } finally {
      setExporting(false);
    }
  }, []);

  const getLevel = (m: Module): ModuleLevel => m.moduleLevel ?? (m.isTopModule ? 'module' : 'chapter');

  if (isLoading && !data) return <div className="loading">Loading...</div>;
  if (error && !data) return <div className="error">Error: {(error as Error).message}</div>;

  return (
    <ModulesTableView
      modules={modulesWithCounts}
      subject={filterSubject}
      onSubjectChange={setFilterSubject}
      subjectOptions={[...ALL_SUBJECT_OPTIONS]}
      grade={filterGrade}
      onGradeChange={setFilterGrade}
      loading={isFetching}
      hideQuestionColumn={!countsData}
      totalCount={data?.modules.length ?? 0}
      onRowClick={(m) => handleEditClick(m as Module)}
      toolbarActions={
        <>
          {canImportExport && (
            <>
              <button className="btn-secondary" onClick={handleExport} disabled={exporting}>
                {exporting ? 'Exporting...' : 'Export'}
              </button>
              <button className="btn-secondary" onClick={() => setImportDialogOpen(true)}>Import</button>
            </>
          )}
          {canAuthorModules && (
            <button className="btn-primary" onClick={handleCreateNew}>+ New Module</button>
          )}
        </>
      }
      banner={saveStatus && (
        <div className={`save-status ${saveStatus.type}`}>
          {saveStatus.message}
          <button className="status-dismiss" onClick={() => setSaveStatus(null)}>×</button>
        </div>
      )}
    >
      <ModuleImportDialog
        isOpen={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onImported={() => queryClient.invalidateQueries({ queryKey: ['modules'] })}
      />

      {/* Edit/Create Modal */}
      {(editingModule || isCreating) && editForm && (
        <div className="modal-overlay">
          <div className="modal-content module-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{isCreating ? 'Create New Module' : 'Edit Module'}</h2>
              <button className="close-btn" onClick={handleCancel}>×</button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group half">
                  <label>Readable ID *</label>
                  <input
                    type="text"
                    value={editForm.readableId || ''}
                    onChange={(e) => handleFormChange('readableId', e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    placeholder="e.g., newtons_laws"
                    className="form-input"
                  />
                  <p className="form-help">Lowercase alphanumeric with underscores only</p>
                </div>
                <div className="form-group half">
                  <label>Display Name *</label>
                  <input
                    type="text"
                    value={editForm.displayName || ''}
                    onChange={(e) => handleFormChange('displayName', e.target.value)}
                    placeholder="e.g., Newton's Laws of Motion"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group half">
                  <label>Subject *</label>
                  <select
                    value={editForm.subject || 'maths'}
                    onChange={(e) => handleFormChange('subject', e.target.value)}
                  >
                    {ALL_SUBJECT_OPTIONS.map(s => (
                      <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group half">
                  <label>Sort Order</label>
                  <IntegerInput
                    value={editForm.sortOrder || 0}
                    onChange={(v) => { if (v != null) handleFormChange('sortOrder', v); }}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Grade × Board *</label>
                <p className="form-help">
                  Which boards/exams teach this topic at which grade (e.g. CBSE/STATE at 10, ICSE at 9).
                  This is the module's only grade record — a module with no rows is taught nowhere and
                  drops out of every grade-filtered list. The separate Grades checkboxes that used to sit
                  above were a board-blind second copy of the same fact, and drifted from it.
                </p>
                <div className="gradeboards-editor">
                  {(editForm.gradeBoards ?? []).map((row, i) => (
                    <div key={i} className="gradeboard-row">
                      <select
                        className="gradeboard-grade"
                        value={row[0]}
                        onChange={(e) => setGradeBoardGrade(i, e.target.value as Grade)}
                      >
                        {GRADE_OPTIONS.map(g => <option key={g} value={g}>Class {g}</option>)}
                      </select>
                      <div className="checkbox-group gradeboard-exams">
                        {REGISTRABLE_PROGRAMME_OPTIONS.map(ex => (
                          <label key={ex} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={row[1].includes(ex)}
                              onChange={() => toggleGradeBoardExam(i, ex)}
                            />
                            {ex}
                          </label>
                        ))}
                      </div>
                      <button
                        type="button"
                        className="btn-remove-instruction"
                        title="Remove grade row"
                        onClick={() => removeGradeBoardRow(i)}
                      >×</button>
                    </div>
                  ))}
                  <button type="button" className="btn-add-instruction" onClick={addGradeBoardRow}>+ Add grade</button>
                </div>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={editForm.description || ''}
                  onChange={(e) => handleFormChange('description', e.target.value)}
                  placeholder="Brief description of the module..."
                  rows={3}
                  className="form-textarea"
                />
              </div>

              {/* Module Instructions (key-value pairs for prompt placeholders) */}
              <div className="form-group">
                <label>Prompt Instructions</label>
                <p className="form-help">
                  Named instructions injected into prompts via {'{{module.instructions}}'} (all) or {'{{module.instructions.KEY}}'} (specific).
                </p>
                <div className="instructions-editor">
                  {Object.entries(editForm.instructions || {}).map(([key, value]) => (
                    <div key={key} className="instruction-row">
                      <input
                        type="text"
                        value={key}
                        className="form-input instruction-key"
                        placeholder="key"
                        onChange={(e) => {
                          const newKey = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                          if (newKey === key) return;
                          const instr = { ...(editForm.instructions || {}) };
                          delete instr[key];
                          instr[newKey] = value;
                          handleFormChange('instructions', instr);
                        }}
                      />
                      <textarea
                        value={value}
                        className="form-textarea instruction-value"
                        placeholder="Instruction text..."
                        rows={2}
                        onChange={(e) => {
                          const instr = { ...(editForm.instructions || {}) };
                          instr[key] = e.target.value;
                          handleFormChange('instructions', instr);
                        }}
                      />
                      <button
                        className="btn-remove-instruction"
                        title="Remove instruction"
                        onClick={() => {
                          const instr = { ...(editForm.instructions || {}) };
                          delete instr[key];
                          handleFormChange('instructions', Object.keys(instr).length > 0 ? instr : undefined);
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    className="btn-add-instruction"
                    onClick={() => {
                      const instr = { ...(editForm.instructions || {}) };
                      // Generate unique key
                      let newKey = 'new_instruction';
                      let i = 1;
                      while (newKey in instr) { newKey = `new_instruction_${i++}`; }
                      instr[newKey] = '';
                      handleFormChange('instructions', instr);
                    }}
                  >
                    + Add Instruction
                  </button>
                </div>
              </div>

              <ModulePitfallsEditor
                pitfalls={editForm.pitfalls}
                onChange={(pitfalls) => handleFormChange('pitfalls', pitfalls)}
                moduleId={editForm.id}
                parentModuleId={editForm.parentModuleId}
                allModules={data?.modules ?? []}
              />

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={editForm.isActive ?? true}
                    onChange={(e) => handleFormChange('isActive', e.target.checked)}
                  />
                  Active
                </label>
              </div>

              {/* Module Level */}
              <div className="form-group">
                <label>Module Level</label>
                <select
                  value={editForm.moduleLevel ?? (editForm.isTopModule ? 'module' : 'chapter')}
                  onChange={(e) => {
                    const level = e.target.value as ModuleLevel;
                    setEditForm(prev => {
                      if (!prev) return prev;
                      return {
                        ...prev,
                        moduleLevel: level,
                        isTopModule: level === 'module',
                        parentModuleId: level === 'module' ? undefined : prev.parentModuleId,
                      };
                    });
                  }}
                >
                  <option value="module">Module (top-level)</option>
                  <option value="chapter">Chapter</option>
                  <option value="topic">Topic</option>
                </select>
                <p className="form-help">Module → Chapter → Topic hierarchy</p>
              </div>

              <div className="form-group">
                <label>Parent {(editForm.moduleLevel ?? 'chapter') === 'topic' ? 'Chapter' : 'Module'}</label>
                <select
                  value={editForm.parentModuleId || ''}
                  onChange={(e) => handleFormChange('parentModuleId', e.target.value || undefined)}
                  disabled={(editForm.moduleLevel ?? (editForm.isTopModule ? 'module' : 'chapter')) === 'module'}
                >
                  <option value="">No parent (standalone)</option>
                  {(editForm.moduleLevel ?? 'chapter') === 'topic'
                    ? /* Topics: show chapters as parents */
                      data?.modules
                        ?.filter(m => m.subject === editForm.subject && m.id !== editForm.id && getLevel(m) === 'chapter')
                        .map(m => (
                          <option key={m.id} value={m.id}>{m.displayName}</option>
                        ))
                    : /* Chapters: show modules as parents */
                      data?.modules
                        ?.filter(m => m.subject === editForm.subject && m.id !== editForm.id && getLevel(m) === 'module')
                        .map(m => (
                          <option key={m.id} value={m.id}>{m.displayName}</option>
                        ))
                  }
                </select>
                <p className="form-help">
                  {(editForm.moduleLevel ?? 'chapter') === 'topic'
                    ? 'Select parent chapter'
                    : 'Select parent module (only top-level modules shown)'}
                </p>
              </div>
            </div>

            <div className="modal-footer">
              {editingModule && (
                <button
                  className="btn-delete"
                  onClick={() => {
                    if (editingModule) {
                      handleDeleteClick(editingModule);
                      handleCancel();
                    }
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
                </button>
              )}
              <div className="footer-spacer" />
              <button className="btn-cancel" onClick={handleCancel}>
                Cancel
              </button>
              <button
                className="btn-save"
                onClick={handleSave}
                disabled={saveMutation.isPending || !editForm.readableId || !editForm.displayName || !editForm.grades?.length}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save Module'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModulesTableView>
  );
}
